// @flow
import { useMemo } from 'react';
import { connect } from 'react-redux';
import useMavenWidget from '../../hooks/useMavenWidget';

type Props = {
  requiredPermission?: string,
  organizationId?: string,
  agentId?: string,
  bgColor?: string,
  textColor?: string,
  horizontalPosition?: 'left' | 'right',
  verticalPosition?: 'top' | 'bottom',
  includeUserData?: boolean,
  currentUser?: Object,
};

/**
 * MavenWidget Component
 *
 * A component that initializes the Maven AGI chat widget with feature flag
 * and permission controls. The widget is loaded conditionally based on:
 * 1. LaunchDarkly feature flag (maven-agi)
 * 2. User permissions (optional)
 *
 * @example
 * // Basic usage with feature flag only
 * <MavenWidget />
 *
 * @example
 * // With custom configuration
 * <MavenWidget
 *   bgColor="#1976d2"
 *   textColor="white"
 *   horizontalPosition="left"
 * />
 *
 * @example
 * // With permission check
 * <MavenWidget requiredPermission="customer-support-chat" />
 */
const MavenWidget = ({
  requiredPermission,
  organizationId = 'fortressos',
  agentId = 'support',
  bgColor,
  textColor,
  horizontalPosition,
  verticalPosition,
  includeUserData = true,
  currentUser,
}: Props) => {
  // Build user data for context if enabled
  const unsignedUserData = useMemo(() => {
    if (!includeUserData || !currentUser) return undefined;

    return {
      userId: currentUser?.user?.id || '',
      email: currentUser?.user?.emailAddress || '',
      firstName: currentUser?.user?.firstName || '',
      lastName: currentUser?.user?.lastName || '',
      username: currentUser?.user?.username || '',
      isAdmin: currentUser?.user?.isAdminUser || false,
    };
  }, [includeUserData, currentUser]);

  // Build widget config
  const config = useMemo(() => {
    const widgetConfig = {
      organizationId,
      agentId,
      bgColor: bgColor || undefined,
      textColor: textColor || undefined,
      horizontalPosition: horizontalPosition || undefined,
      verticalPosition: verticalPosition || undefined,
      unsignedUserData: unsignedUserData || undefined,
    };

    return widgetConfig;
  }, [
    organizationId,
    agentId,
    bgColor,
    textColor,
    horizontalPosition,
    verticalPosition,
    unsignedUserData,
  ]);

  // Initialize widget with hook
  useMavenWidget({
    enabled: true,
    requiredPermission,
    config,
    currentUser,
  });

  // This component doesn't render anything - the widget is injected by Maven
  return null;
};

const mapStateToProps = (state: Object): Object => ({
  currentUser: state?.app?.currentUser || null,
});

export default (connect(mapStateToProps)(MavenWidget): any);
